import { useLayoutEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core';

/**
 * The footer the public revenexx portals share (DX-352).
 *
 * One row under the frame: the other revenexx surfaces on the left, the
 * company and its legal pages on the right. The list, its order and its
 * addresses are those of the Developer Portal's `AppFooter.vue`, which Help and
 * Schemas carry verbatim — a colleague who uses the footer on one of them finds
 * the same row here.
 *
 * It replaces the sidebar footer the target picture drew ("Roadmap", "Design
 * tokens & components", DX-302), which none of the public portals has either.
 *
 * MUI v4 `makeStyles` only — no MUI v5.
 */

type FooterLink = { label: string; href: string };

const SITES: FooterLink[] = [
  { label: 'Developer Portal', href: 'https://www.revenexx.dev' },
  { label: 'Help', href: 'https://help.revenexx.com' },
  { label: 'Cover', href: 'https://cover.revenexx.design' },
  { label: 'Studio', href: 'https://studio.revenexx.design' },
  { label: 'Schemas', href: 'https://schemas.revenexx.com' },
  { label: 'Changelog', href: 'https://changelog.revenexx.com' },
  { label: 'Status', href: 'https://status.revenexx.com' },
  { label: 'Support', href: 'https://support.revenexx.com' },
  { label: 'About revenexx', href: 'https://www.revenexx.com' },
];

const LEGAL: FooterLink[] = [
  { label: 'Privacy Policy', href: 'https://www.revenexx.com/privacy' },
  { label: 'Terms and Conditions', href: 'https://www.revenexx.com/terms' },
];

const useStyles = makeStyles(theme => ({
  footer: {
    borderTop: '1px solid var(--ui-footer-border)',
    background: 'var(--ui-footer-bg)',
    paddingInline: 'var(--ui-shell-gutter)',
    // At least the height the frame subtracts for it, so the two add up to
    // the window; more only where the row wraps on a narrow screen.
    minHeight: 'var(--ui-footer-height)',
    boxSizing: 'border-box',
    // Below 600px Backstage draws its menu as a bar fixed to the bottom of
    // the window, and the footer is the last thing on the page — without
    // this, its last row sits underneath that bar.
    [theme.breakpoints.down('xs')]: {
      paddingBottom: 56,
    },
  },
  inner: {
    maxWidth: 'var(--ui-shell-max)',
    margin: '0 auto',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: '1rem',
    rowGap: '0.375rem',
    padding: '0.75rem 0',
  },
  group: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: '1rem',
    rowGap: '0.375rem',
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  link: {
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    color: 'var(--ui-footer-text)',
    textDecoration: 'none',
    '&:hover': {
      color: 'var(--ui-footer-text-hover)',
    },
  },
  copyright: {
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    color: 'var(--ui-footer-text)',
  },
}));

/**
 * Publishes the footer's real height as `--ui-footer-measured`, which the
 * frame subtracts from the window (Root.tsx). `--ui-footer-height` is the one
 * row the footer takes on a wide screen; below about 1180px the row wraps,
 * and a frame that went on subtracting one row made the page scroll as well
 * as the frame's content — two scrollbars, one inside the other.
 */
const useMeasuredHeight = () => {
  const ref = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const footer = ref.current;
    if (!footer || typeof ResizeObserver === 'undefined') return undefined;
    const root = document.documentElement.style;
    const observer = new ResizeObserver(() => {
      root.setProperty('--ui-footer-measured', `${footer.offsetHeight}px`);
    });
    observer.observe(footer);
    return () => {
      observer.disconnect();
      root.removeProperty('--ui-footer-measured');
    };
  }, []);
  return ref;
};

export const PortalFooter = () => {
  const classes = useStyles();
  const ref = useMeasuredHeight();

  return (
    <footer ref={ref} className={classes.footer}>
      <div className={classes.inner}>
        <nav aria-label="revenexx sites">
          <ul className={classes.group}>
            {SITES.map(site => (
              <li key={site.label}>
                <a
                  className={classes.link}
                  href={site.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {site.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
        <ul className={classes.group}>
          <li className={classes.copyright}>
            © {new Date().getFullYear()} revenexx GmbH
          </li>
          {LEGAL.map(link => (
            <li key={link.label}>
              <a
                className={classes.link}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </footer>
  );
};
